import type {
  ChatShortcutAction,
  ChatShortcutEntityType,
  IChatShortcutMetadata,
} from "@/types/(my-space)/chat.interface";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { COMMON_PATHS } from "@/routes/common/routes.common";
import { MS_ASSETS_PATHS } from "@/routes/my-space/routes.asset";
import { MS_DOCUMENTS_PATHS } from "@/routes/my-space/routes.documents";
import { MS_EVENT_PATHS } from "@/routes/my-space/routes.events";
import { MS_EXPENSES_PATHS } from "@/routes/my-space/routes.expenses";
import { MS_TASKS_PATHS } from "@/routes/my-space/routes.tasks";
import { MS_LEAVES_MANAGEMENT_PATHS } from "@/routes/my-space/routes.leave-management";
import { COMPANY_ANNOUNCEMENTS_PATHS } from "@/routes/company/routes.announcement";
import { COMPANY_ASSETS_PATHS } from "@/routes/company/routes.asset";
import { COMPANY_DOCUMENTS_PATHS } from "@/routes/company/routes.documents";
import { COMPANY_EVENT_PATHS } from "@/routes/company/routes.events";
import { COMPANY_EXPENSES_PATHS } from "@/routes/company/routes.expenses";
import { COMPANY_LEAVES_MANAGEMENT_PATHS } from "@/routes/company/routes.leave-management";
import { COMPANY_TASKS_PATHS } from "@/routes/company/routes.tasks";
import { chatShortcutFor } from "./chat-shortcuts";

/**
 * Where tapping a Shortcut card goes:
 * - `path` — navigate there;
 * - `no_access` — a specific item the viewer may not open (toast, stay);
 * - `unknown` — a shortcut this build doesn't understand (card is inert).
 */
export type ShortcutTarget =
  | { kind: "path"; path: string }
  | { kind: "no_access" }
  | { kind: "unknown" };

/** The company-level permission section that guards each kind's company pages. */
export const SHORTCUT_ENTITY_SECTION: Record<ChatShortcutEntityType, PLATFORM_SECTION_ENUM> = {
  leave_request: PLATFORM_SECTION_ENUM.LEAVE_MANAGEMENT,
  document_request: PLATFORM_SECTION_ENUM.DOCUMENTS,
  expense: PLATFORM_SECTION_ENUM.EXPENSES,
  asset_request: PLATFORM_SECTION_ENUM.ASSETS,
  event: PLATFORM_SECTION_ENUM.EVENTS,
  announcement: PLATFORM_SECTION_ENUM.ANNOUNCEMENTS,
  task: PLATFORM_SECTION_ENUM.PROJECT_MANAGEMENT,
};

/**
 * The employee form each action opens; the dashboard hosts the clock button.
 * A function, not a module-scope map: the route files sit on an import cycle
 * with the pages (routes → page → … → chat), so their constants must be read
 * at call time, never while this module is being evaluated.
 */
export const shortcutActionPath = (action: ChatShortcutAction): string | null => {
  switch (action) {
    case "apply_leave":
      return MS_LEAVES_MANAGEMENT_PATHS.APPLY;
    case "request_document":
      return MS_DOCUMENTS_PATHS.REQUEST;
    case "submit_expense":
      return MS_EXPENSES_PATHS.CREATE;
    case "request_asset":
      return MS_ASSETS_PATHS.REQUEST;
    case "clock_in":
      // The dashboard (no path constant exists for it).
      return "/";
    default:
      return null;
  }
};

export interface ShortcutViewer {
  myUserId: number | undefined;
  /** Company-level `view` on that section (super admins: always). */
  canViewCompany: (section: PLATFORM_SECTION_ENUM) => boolean;
}

const path = (p: string): ShortcutTarget => ({ kind: "path", path: p });
const NO_ACCESS: ShortcutTarget = { kind: "no_access" };
const UNKNOWN: ShortcutTarget = { kind: "unknown" };

export const shortcutTarget = (
  shortcut: IChatShortcutMetadata | undefined,
  viewer: ShortcutViewer,
): ShortcutTarget => {
  if (!shortcut) return UNKNOWN;

  if (shortcut.action) {
    const p = shortcutActionPath(shortcut.action);
    return p ? path(p) : UNKNOWN;
  }

  const e = shortcut.entity;
  if (e) {
    const section = SHORTCUT_ENTITY_SECTION[e.type];
    if (!section) return UNKNOWN;
    const isOwner = viewer.myUserId != null && e.ownerId === viewer.myUserId;
    const isCompany = viewer.canViewCompany(section);
    switch (e.type) {
      case "leave_request":
        if (isOwner) return path(MS_LEAVES_MANAGEMENT_PATHS.VIEW(e.id));
        return isCompany ? path(COMPANY_LEAVES_MANAGEMENT_PATHS.LEAVES_SUMMARY(e.id)) : NO_ACCESS;
      case "document_request":
        if (isOwner) return path(MS_DOCUMENTS_PATHS.SENT_REQUEST_VIEW(e.id));
        return isCompany ? path(COMPANY_DOCUMENTS_PATHS.SENT_REQUEST_VIEW(e.id)) : NO_ACCESS;
      case "expense":
        if (isOwner) return path(MS_EXPENSES_PATHS.DETAIL(e.id));
        return isCompany ? path(COMPANY_EXPENSES_PATHS.DETAIL(e.id)) : NO_ACCESS;
      case "asset_request":
        if (isOwner) return path(MS_ASSETS_PATHS.REQUESTED_ASSET_VIEW(e.id));
        return isCompany ? path(COMPANY_ASSETS_PATHS.ASSET_REQUEST_VIEW(e.id)) : NO_ACCESS;
      case "event":
        // `id` is the SENDER's invitation; mine isn't known, so a fellow
        // invitee lands on their events list.
        if (isOwner) return path(MS_EVENT_PATHS.VIEW(e.id));
        if (isCompany && e.eventId != null) return path(COMPANY_EVENT_PATHS.VIEW(e.eventId));
        return path(MS_EVENT_PATHS.ROOT);
      case "announcement":
        // Company-wide: anyone may open it. The portal has no employee
        // announcement detail, so the list it's on.
        return isCompany
          ? path(COMPANY_ANNOUNCEMENTS_PATHS.VIEW(e.id))
          : path(COMMON_PATHS.ANNOUNCEMENTS_ALL);
      case "task":
        // Not just the sender's: anyone on the project may read it. Whether
        // the viewer is isn't known here — the task page checks and says so.
        if (e.projectId == null) return path(MS_TASKS_PATHS.TASK_REDIRECT(e.id));
        return isCompany
          ? path(COMPANY_TASKS_PATHS.DETAIL(e.projectId, e.id))
          : path(MS_TASKS_PATHS.DETAIL(e.projectId, e.id));
      default:
        return UNKNOWN;
    }
  }

  const module = chatShortcutFor(shortcut.route);
  return module ? path(module.path) : UNKNOWN;
};
